import type { ReactNode } from 'react';
import { Button } from '../../basic/Button/Button';
import { ExportButton } from '../ExportButton/ExportButton';
import styles from './FiltersPanel.module.css';

export interface FiltersPanelProps {
  /**
   * Controles de filtro (buscador, selects, rango de fechas, etc.).
   */
  children: ReactNode;
  /**
   * Se dispara al presionar "Aplicar filtros".
   */
  onApply: () => void;
  /**
   * Se dispara al presionar "Limpiar filtros".
   */
  onClear: () => void;
  /**
   * Si hay al menos un criterio cargado. Controla el estado del botón "Aplicar filtros"
   * (deshabilitado cuando es false).
   */
  hasCriteria: boolean;
  /**
   * Muestra el loader en "Aplicar filtros" mientras se resuelve la consulta.
   */
  isApplying?: boolean;
  /**
   * Título opcional del panel.
   */
  title?: string;
  applyLabel?: string;
  clearLabel?: string;
  /**
   * Si se provee, se renderiza el control "Descargar" (con loader) a la derecha.
   */
  onExportExcel?: () => void | Promise<void>;
  /**
   * Si se provee, el control "Descargar" abre un menú contextual con Excel y PDF.
   * Seam BF-643/BF-644 (el BE aún no expone el endpoint de PDF).
   */
  onExportPdf?: () => void | Promise<void>;
  /**
   * Muestra la opción PDF deshabilitada (p. ej. mientras el BE no expone el endpoint).
   */
  pdfDisabled?: boolean;
  /**
   * Habilita el control de export según si hay resultados. Por defecto true.
   */
  hasResults?: boolean;
  /**
   * Texto del botón de export. Por defecto "Descargar".
   */
  exportLabel?: string;
  /**
   * Texto de la opción Excel del menú. Por defecto "Excel".
   */
  exportExcelLabel?: string;
  /**
   * Texto de la opción PDF del menú. Por defecto "PDF".
   */
  exportPdfLabel?: string;
  /**
   * Acciones extra a colocar antes de "Aplicar filtros" (p. ej. el botón "Descargar PDF").
   */
  extraActions?: ReactNode;
  className?: string;
}

export function FiltersPanel({
  children,
  onApply,
  onClear,
  hasCriteria,
  isApplying = false,
  title,
  applyLabel = 'Aplicar filtros',
  clearLabel = 'Limpiar filtros',
  onExportExcel,
  onExportPdf,
  pdfDisabled,
  hasResults,
  exportLabel,
  exportExcelLabel,
  exportPdfLabel,
  extraActions,
  className,
}: FiltersPanelProps) {
  const classes = [styles.panel, className].filter(Boolean).join(' ');
  const clearClassName = hasCriteria ? undefined : styles.clearButtonInactive;

  return (
    <section className={classes} aria-label={title ? undefined : 'Filtros'}>
      {title && <h3 className={styles.title}>{title}</h3>}
      <div className={styles.controls}>{children}</div>
      <div className={styles.actions}>
        <Button type="button" variant="link" className={clearClassName} onClick={onClear} disabled={isApplying}>
          {clearLabel}
        </Button>

        <div className={styles.actionsRight}>
          {onExportExcel && (
            <ExportButton
              onExportExcel={onExportExcel}
              onExportPdf={onExportPdf}
              pdfDisabled={pdfDisabled}
              hasResults={hasResults}
              label={exportLabel}
              excelLabel={exportExcelLabel}
              pdfLabel={exportPdfLabel}
            />
          )}
          {extraActions}
          <Button
            type="button"
            variant="primary"
            onClick={onApply}
            disabled={!hasCriteria}
            isLoading={isApplying}
          >
            {applyLabel}
          </Button>
        </div>
      </div>
    </section>
  );
}

FiltersPanel.displayName = 'FiltersPanel';
